import { useMemo, useState, useCallback } from 'react';
import { api } from '../../../core/api/queries';
import { useReportPage } from '../../../shared/hooks';
import { ReportPageLayout } from '../../../shared/components/layouts';
import { normalizeSchoolName } from '../../../shared/components/tables';
import { EditionSelector } from '../../../shared/components/tables';
import { useSchoolRequestsMutations } from '../hooks/use-school-requests-mutations';
import { columnBuilder, formatUnixTimestamp } from '../../../shared/utils';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import { CodSeLaAprobare } from '../components/CodSeLaAprobare';
import { ApprovalDialog } from '../components/ApprovalDialog';
import { PhoneNotesDialog } from '../components/PhoneNotesDialog';
import { FormPreviewDialog } from '../components/FormPreviewDialog';
import { EditableFormDialog } from '../components/EditableFormDialog';
import { eroriCorectareFormular } from '../utils/erori-corectare-formular';
import { EmailChangeDialog } from '../components/EmailChangeDialog';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { IstoricProfesoriDialog } from '../components/IstoricProfesoriDialog';
import { ActionButton } from '../../../shared/components/buttons/ActionButton';
import type { SchoolRequestReportDto, SchoolRequestDto } from '../api/school-requests.api';
import { useCounties } from '../../../shared/hooks/use-counties';
import { schoolRequestsApi } from '../api/school-requests.api';
import { PRELUARE_CONFIG } from '@/constants/config';
import { filtruPentruExportProfesori } from '../utils/filtru-export-profesori';
import { etichetaNumarIstoric } from '../utils/istoric-profesori';
import { trackSchoolRequestReviewed } from '@/telemetry/posthog-events';

/**
 * School Requests Report Page
 *
 * Admin page for managing school registration requests with approval workflow.
 *
 * **Features**:
 * - 20+ columns including status, professor info, school details
 * - 6 action columns: approval, notes, form preview/edit, email change, teacher history, delete
 * - 7 dialogs for different workflows (incl. delete confirm + teacher history, B4)
 * - Complex role-based business logic
 * - Async form data loading
 * - DateRange filters for timestamps
 *
 * **Architecture** (Refactored 2025-11-01):
 * - Uses useReportPage hook (consolidates all state management)
 * - Uses ReportPageLayout component (consolidates JSX structure)
 * - Uses columnBuilder for column definitions (already implemented)
 * - Uses separate mutations hook for all actions
 * - Reduced from 428 lines to ~345 lines (19% reduction, 83 lines eliminated)
 *
 * **Complexity**: ⭐⭐⭐ High
 * - 20+ columns
 * - 5 separate dialogs (2 with shared form data state)
 * - 6 mutations
 * - Async form data loading
 * - Complex conditional action columns
 * - DateRange filters
 */
export function SchoolRequestsReportPage() {
    // ==========================================
    // 1. DATA FETCHING & REPORT INTERFACE
    // ==========================================

    const { report, adapters, handleExport, editions, currentEdition, changeEdition } = useReportPage({
        queryHook: api.schoolRequests.report,
        initialSort: ['timestamp,desc'],
        exportEndpoint: '/api/users/school-requests/report',
        exportFilename: 'raport_profesori',
        withEditions: true,  // Enable edition filtering for ADMIN users
        // Exportul merge pe ProfesorReportDTO, nu pe DTO-ul grilei: fara traducere, filtrele
        // grilei erau sarite si exportul iesea cu toti profesorii editiei.
        mapExportFilter: filtruPentruExportProfesori,
    });

    // ==========================================
    // 2. SHARED HOOKS
    // ==========================================

    const mutations = useSchoolRequestsMutations();
    const { data: counties = [] } = useCounties();

    // ==========================================
    // 3. DIALOG STATE (5 dialogs)
    // ==========================================

    const [selectedRequest, setSelectedRequest] = useState<SchoolRequestReportDto | null>(null);
    const [selectedFormData, setSelectedFormData] = useState<any>(null);
    const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
    const [phoneNotesDialogOpen, setPhoneNotesDialogOpen] = useState(false);
    const [formPreviewDialogOpen, setFormPreviewDialogOpen] = useState(false);
    const [editFormDialogOpen, setEditFormDialogOpen] = useState(false);
    const [emailChangeDialogOpen, setEmailChangeDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<SchoolRequestReportDto | null>(null);
    const [istoricPentru, setIstoricPentru] = useState<SchoolRequestReportDto | null>(null);
    const [loadingFormData, setLoadingFormData] = useState(false);
    // Codul SE al randului din grila: pe o cerere in asteptare, backendul pune acolo codul scolii
    // gasite dupa SIIIR. Formularul incarcat separat are doar codul de pe cerere, gol pana la aprobare.
    const [codSeRand, setCodSeRand] = useState<string | null>(null);
    // Profesorul randului: formularul incarcat separat are doar `professorId`, nu si numele/telefonul.
    const [profesorRand, setProfesorRand] = useState<SchoolRequestReportDto['professor'] | null>(null);
    // Erorile serverului pe campurile din dialogul de corectare, ca sa apara SUB camp, nu doar in toast.
    const [eroriCorectare, setEroriCorectare] = useState<Record<string, string>>({});

    // ==========================================
    // 4. ACTION HANDLERS
    // ==========================================

    const handleApprove = useCallback((request: SchoolRequestReportDto) => {
        mutations.approveRequest.mutate(request.id!, {
            onSuccess: () => {
                trackSchoolRequestReviewed({ decision: 'approved', teacherId: request.professor?.id, isTakeover: request.cererePreluare });
                setApprovalDialogOpen(false);
            }
        });
    }, [mutations]);

    const handleReject = useCallback((request: SchoolRequestReportDto, reason: string) => {
        mutations.rejectRequest.mutate({ schoolRequestId: request.id!, rejectReason: reason }, {
            onSuccess: () => {
                trackSchoolRequestReviewed({ decision: 'rejected', teacherId: request.professor?.id, isTakeover: request.cererePreluare });
                setApprovalDialogOpen(false);
            }
        });
    }, [mutations]);

    const handleSavePhoneNote = useCallback((userId: string, note: string) => {
        mutations.updatePhoneNote.mutate({ userId, phoneCallNote: note }, {
            onSuccess: () => setPhoneNotesDialogOpen(false)
        });
    }, [mutations]);

    const handleSaveFormData = useCallback((data: SchoolRequestDto) => {
        setEroriCorectare({});
        mutations.updateFormData.mutate(data, {
            onSuccess: () => setEditFormDialogOpen(false),
            onError: (error) => setEroriCorectare(eroriCorectareFormular(error).vizibile),
        });
    }, [mutations]);

    const handleReassignProfessor = useCallback((schoolRequestId: string, newEmail: string) => {
        mutations.reassignProfessor.mutate({ schoolRequestId, newProfessorEmail: newEmail }, {
            onSuccess: () => setEmailChangeDialogOpen(false)
        });
    }, [mutations]);

    /* `window.confirm()` inlocuit cu un dialog al aplicatiei: caseta nativa nu
       respecta tokenii si pragul de 44px, blocheaza firul (deci si canalul CDP,
       ceea ce ingheata orice harness) si nu exista in DOM, deci nu se poate
       verifica pe ecran. */
    const handleDelete = useCallback((row: SchoolRequestReportDto) => {
        setDeleteTarget(row);
    }, []);

    const confirmDelete = useCallback(() => {
        if (!deleteTarget?.id) return;
        mutations.deleteRequest.mutate(deleteTarget.id, {
            onSettled: () => setDeleteTarget(null),
        });
    }, [mutations, deleteTarget]);

    /**
     * Async form data loading handler
     * Loads full form data from API before opening preview/edit dialog
     */
    const openFormDialog = useCallback(async (userId: string | undefined, editable: boolean, codSe?: string | null, profesor?: SchoolRequestReportDto['professor'] | null) => {
        if (!userId) return;

        setCodSeRand(codSe || null);
        setProfesorRand(profesor ?? null);
        setEroriCorectare({});
        setLoadingFormData(true);
        try {
            const formData = await schoolRequestsApi.getByUserId(userId);
            setSelectedFormData(formData);

            if (editable) {
                setEditFormDialogOpen(true);
            } else {
                setFormPreviewDialogOpen(true);
            }
        } catch (error: unknown) {
            console.error('Error loading form data:', error);
            alert('Eroare la încărcarea formularului');
        } finally {
            setLoadingFormData(false);
        }
    }, []);

    const actionsDisabled = report.loading || mutations.isLoading || loadingFormData;

    // ==========================================
    // 5. COLUMN DEFINITIONS (20+ columns)
    // ==========================================

    const columns = useMemo(() =>
        columnBuilder<SchoolRequestReportDto>()
            // ===== STATUS BADGE =====
            .custom({
                key: 'approvalStatus',
                label: 'Status Aprobare',
                sortable: true,
                filterable: true,
                filterPlaceholder: 'Status...',
                filterType: 'text',
                render: (_, row) => <ApprovalStatusBadge status={row.approvalStatus} />
            })

            // ===== ACTION COLUMNS (5 total) =====
            .action('actions', 'Acțiune', (_value, row) => (
                <ActionButton
                    intent="primary"
                    onClick={() => { setSelectedRequest(row); setApprovalDialogOpen(true); }}
                    disabled={actionsDisabled || row.approvalStatus !== 'PENDING'}
                >
                    Aprobă/Respinge
                </ActionButton>
            ))
            .action('notes_action', 'Acțiune Notițe', (_value, row) => (
                <ActionButton
                    intent="ghost"
                    onClick={() => { setSelectedRequest(row); setPhoneNotesDialogOpen(true); }}
                    disabled={actionsDisabled}
                >
                    Adaugă Notițe
                </ActionButton>
            ))
            .action('form_action', 'Formular de Înscriere', (_value, row) => (
                <div className="flex gap-1">
                    {row.professor?.hasSchoolRegistrationFormCompleted && (
                        <>
                            <ActionButton
                                intent="ghost"
                                onClick={() => openFormDialog(row.professor?.id, false, row.codSe)}
                                disabled={actionsDisabled}
                            >
                                Previzualizare
                            </ActionButton>
                            {!row.professor?.isEnabled && row.professor?.hasSchoolRegistrationFormCompleted && (
                                <>
                                    <span>/</span>
                                    <ActionButton
                                        intent="ghost"
                                        onClick={() => openFormDialog(row.professor?.id, true, row.codSe, row.professor)}
                                        disabled={actionsDisabled}
                                    >
                                        Corectare
                                    </ActionButton>
                                </>
                            )}
                        </>
                    )}
                </div>
            ))
            .action('email_action', 'Schimbă profesorul', (_value, row) => (
                <ActionButton
                    intent="ghost"
                    onClick={() => { setSelectedRequest(row); setEmailChangeDialogOpen(true); }}
                    disabled={actionsDisabled}
                >
                    Schimbă
                </ActionButton>
            ))

            // ===== PRELUARE (arbitraj intre doi profesori pe aceeasi scoala) =====
            // Toate patru sunt .action(): sortable=false + filterable=false.
            // Campurile nu exista pe entitatea SchoolRequest (sunt populate post-mapare in
            // service), iar GenericSpecification face root.get(numeCamp) pe entitate pentru
            // orice filtru non-null -> un filtru pe ele ar da 500.
            .action('cererePreluare', 'Preluare', (_value, row) =>
                row.cererePreluare ? <span className="font-semibold text-red-600">DA</span> : '-'
            )
            .action('profesorActualNume', 'Profesor actual (școală)', (_value, row) =>
                row.profesorActualNume || '-'
            )
            .action('profesorActualUltimaActivitate', 'Ultima activitate prof. actual', (_value, row) =>
                row.profesorActualUltimaActivitate
                    ? (formatUnixTimestamp(row.profesorActualUltimaActivitate) || '-')
                    : '-'
            )

            // ===== SOLICITANTUL, langa profesorul actual (B4) =====
            // Adminul compara solicitantul cu profesorul scolii fara sa mai derulze 11 coloane.
            .nested('professor.nume', 'Nume solicitant', (row) => row.professor?.nume, { filterPlaceholder: 'Nume...' })
            .nested('professor.email', 'Email solicitant', (row) => row.professor?.email, { filterPlaceholder: 'Email...' })
            .action('istoricProfesori', 'Istoric profesori', (_value, row) => {
                const eticheta = etichetaNumarIstoric(row.istoricProfesoriNumar);
                return eticheta && row.codSiiirUnitate ? (
                    <ActionButton intent="ghost" onClick={() => setIstoricPentru(row)}>
                        {eticheta}
                        {/* Etichetele „3 intrări" se repeta pe randuri: numele scolii le deosebeste pentru cititorul de ecran. */}
                        <span className="sr-only"> în istoricul profesorilor, {row.numeInstitutieInvatamant || row.codSiiirUnitate}</span>
                    </ActionButton>
                ) : '—';
            })
            .action('zileAsteptare', 'Zile în așteptare', (_value, row) => {
                if (!row.timestamp || row.approvalStatus !== 'PENDING') return '-';
                // row.timestamp e declarat Date, dar in realitate e string ISO: transformerele
                // generate nu ruleaza niciodata (docs/knowledge/response-transformers-inert.md).
                const depus = new Date(row.timestamp as unknown as string).getTime();
                if (Number.isNaN(depus)) return '-';
                const zile = Math.floor((Date.now() - depus) / 86_400_000);
                const depasit = row.cererePreluare && zile >= PRELUARE_CONFIG.TERMEN_ZILE;
                return <span className={depasit ? 'font-semibold text-red-600' : undefined}>{zile}</span>;
            })

            // ===== BOOLEAN COLUMNS =====
            .custom({
                key: 'professor.isEnabled',
                label: 'Activat',
                sortable: true,
                filterable: true,
                filterType: 'boolean',
                render: (_, row) => row.professor?.isEnabled ? 'DA' : 'NU'
            })
            .custom({
                key: 'professor.hasSchoolRegistrationFormCompleted',
                label: 'Formular Completat',
                sortable: true,
                filterable: true,
                filterType: 'boolean',
                render: (_, row) => row.professor?.hasSchoolRegistrationFormCompleted ? 'DA' : 'NU'
            })
            .custom({
                key: 'acordGrupWhatsapp',
                label: 'Acord grup WhatsApp',
                sortable: true,
                filterable: true,
                filterType: 'boolean',
                render: (_, row) => row.acordGrupWhatsapp ? 'DA' : 'NU'
            })

            // ===== PHONE NOTES =====
            .custom({
                key: 'professor.phoneCallNote',
                label: 'Notițe Telefonice',
                sortable: true,
                filterable: true,
                filterPlaceholder: 'Notițe...',
                filterType: 'text',
                render: (_, row) => (
                    <div className="max-w-[350px] text-wrap">{row.professor?.phoneCallNote || ''}</div>
                )
            })

            // ===== REJECT REASON =====
            .text('rejectReason', 'Motiv Respingere', 'Motiv...')

            // ===== TIMESTAMPS (DateRange filters) =====
            .custom({
                key: 'professor.timestampInregistrare',
                label: 'Data Prima Înscriere',
                sortable: true,
                filterable: true,
                filterType: 'dateRange',
                dateRangeFromName: 'professor.timestampInregistrare.start',
                dateRangeToName: 'professor.timestampInregistrare.end',
                render: (_, row) => {
                    const timestamp = row.professor?.timestamp;
                    return timestamp ? (formatUnixTimestamp(timestamp) || '-') : '-';
                }
            })
            .custom({
                key: 'timestamp',
                label: 'Data Completare Formular',
                sortable: true,
                filterable: true,
                filterType: 'dateRange',
                dateRangeFromName: 'timestamp.start',
                dateRangeToName: 'timestamp.end',
                render: (value) => value ? (formatUnixTimestamp(value) || '-') : '-'
            })

            // ===== TEXT COLUMNS =====
            .text('telefon', 'Telefon', 'Telefon...')
            // Telefonul din CONTUL profesorului — cel folosit pentru grupul de WhatsApp. `telefon` de mai sus e al instituției.
            .nested('professor.telefon', 'Telefon profesor', (row) => row.professor?.telefon, { filterPlaceholder: 'Telefon...' })
            .custom({
                key: 'numeInstitutieInvatamant',
                label: 'Nume Instituție',
                sortable: true,
                filterable: true,
                filterPlaceholder: 'Instituție...',
                filterType: 'text',
                // `cardRole` face din numele scolii titlul randului in rezumatul de
                // pe mobil. `normalizeSchoolName` normalizeaza DOAR afisarea —
                // filtrul si exportul raman pe textul original.
                cardRole: 'title',
                render: (value) => (
                    <div className="max-w-[350px] text-wrap">{normalizeSchoolName(value) || '-'}</div>
                )
            })
            .text('codSiiirUnitate', 'Cod SIIIR', 'Cod SIIIR...')
            .text('codSe', 'Cod SE', 'Cod SE...', {
                sortable: false,
                // Pe o cerere in asteptare, codul e al scolii gasite dupa SIIIR; gol = scoala noua.
                render: (value, row) =>
                    value || (row.approvalStatus === 'PENDING' ? <CodSeLaAprobare /> : '-'),
            })

            // ===== DELETE ACTION (conditional) =====
            .action('delete', 'ȘTERG', (_value, row) => (
                row.approvalStatus !== 'APPROVED' ? (
                    <ActionButton
                        intent="ghost"
                        onClick={() => handleDelete(row)}
                        disabled={actionsDisabled}
                        className="text-error"
                    >
                        Șterge
                    </ActionButton>
                ) : null
            ))

            .build(),
        [actionsDisabled, openFormDialog, handleDelete]
    );

    // ==========================================
    // 6. RENDER
    // ==========================================

    return (
        <>
            <ReportPageLayout
                title="Raport Înscrieri"
                report={report}
                adapters={adapters}
                handleExport={handleExport}
                columns={columns}
                entityLabel="cereri"
                extraControls={
                    <EditionSelector
                        editions={editions || []}
                        currentEditionId={currentEdition?.id}
                        onChange={changeEdition || (() => {})}
                        disabled={report.loading}
                    />
                }
            />

            {/* Dialogs - Approval/Notes/Email workflows */}
            {selectedRequest && (
                <>
                    <ApprovalDialog
                        isOpen={approvalDialogOpen}
                        onClose={() => { setApprovalDialogOpen(false); setSelectedRequest(null); }}
                        onApprove={() => handleApprove(selectedRequest)}
                        onReject={(reason: string) => handleReject(selectedRequest, reason)}
                        request={selectedRequest}
                        loading={mutations.isApproving || mutations.isRejecting}
                    />

                    <PhoneNotesDialog
                        isOpen={phoneNotesDialogOpen}
                        onClose={() => { setPhoneNotesDialogOpen(false); setSelectedRequest(null); }}
                        onSave={(note) => handleSavePhoneNote(selectedRequest.professor?.id!, note)}
                        initialNote={selectedRequest.professor?.phoneCallNote || ''}
                        professorName={selectedRequest.professor?.nume}
                        loading={mutations.isUpdatingPhoneNote}
                    />

                    <EmailChangeDialog
                        isOpen={emailChangeDialogOpen}
                        onClose={() => { setEmailChangeDialogOpen(false); setSelectedRequest(null); }}
                        onReassign={(newEmail: string) => handleReassignProfessor(selectedRequest.id!, newEmail)}
                        currentEmail={selectedRequest.professor?.email}
                        schoolName={selectedRequest.numeInstitutieInvatamant}
                        loading={mutations.isReassigningProfessor}
                    />
                </>
            )}

            <DeleteConfirmDialog
                isOpen={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
                schoolName={deleteTarget?.numeInstitutieInvatamant}
                loading={mutations.deleteRequest.isPending}
            />

            <IstoricProfesoriDialog
                codSiiirUnitate={istoricPentru?.codSiiirUnitate ?? null}
                numeScoala={istoricPentru?.numeInstitutieInvatamant}
                onClose={() => setIstoricPentru(null)}
            />

            {/* Dialogs - Form preview/edit workflows */}
            {selectedFormData && (
                <>
                    <FormPreviewDialog
                        isOpen={formPreviewDialogOpen}
                        onClose={() => { setFormPreviewDialogOpen(false); setSelectedFormData(null); }}
                        formData={selectedFormData}
                        codSeRand={codSeRand}
                        isLoading={false}
                    />

                    <EditableFormDialog
                        isOpen={editFormDialogOpen}
                        onClose={() => { setEditFormDialogOpen(false); setSelectedFormData(null); }}
                        onSave={handleSaveFormData}
                        formData={selectedFormData}
                        codSeRand={codSeRand}
                        profesor={profesorRand}
                        counties={counties}
                        serverErrors={eroriCorectare}
                        loading={mutations.isUpdatingFormData}
                    />
                </>
            )}
        </>
    );
}
